import React, { useState } from 'react';
import { IoPersonCircle, IoSparkles, IoChevronDown, IoChevronUp, IoPencil, IoRefresh, IoVolumeHigh, IoLanguage } from 'react-icons/io5';
import { CText } from '../../../components/common';
import { Message } from '../../../api/chat';
import { textToSpeech } from '../../../api/sarvam';
import SourceCard from './SourceCard';
import EvaluationTrace from './EvaluationTrace';
import StarRating from './StarRating';

interface ChatMessageProps {
  message: Message;
  colors: any;
  isLastAssistant?: boolean;
  onEdit?: (text: string) => void;
  onRegenerate?: () => void;
  llmResponseEn?: string;
  responseLanguage?: string;
  feedbackRating?: number;
  feedbackComment?: string;
  onFeedback?: (messageId: number, rating: number, comment: string) => Promise<void>;
}

const LANG_LABELS: Record<string, string> = {
  'hi-IN': 'Hindi', 'bn-IN': 'Bengali', 'gu-IN': 'Gujarati', 'kn-IN': 'Kannada',
  'ml-IN': 'Malayalam', 'mr-IN': 'Marathi', 'ta-IN': 'Tamil', 'te-IN': 'Telugu',
  'pa-IN': 'Punjabi', 'en-IN': 'English',
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  message, colors, isLastAssistant, onEdit, onRegenerate,
  llmResponseEn, responseLanguage, feedbackRating, feedbackComment, onFeedback,
}) => {
  const [showSources, setShowSources] = useState(false);
  const [showEnglish, setShowEnglish] = useState(false);
  const [ttsPlaying, setTtsPlaying] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const isUser = message.role === 'user';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Determine display text: show English toggle if translation exists
  const displayText = (!isUser && showEnglish && llmResponseEn) ? llmResponseEn : message.text;
  const hasTranslation = !isUser && !!llmResponseEn && responseLanguage && responseLanguage !== 'en-IN';
  const langLabel = responseLanguage ? (LANG_LABELS[responseLanguage] || responseLanguage) : '';

  const handleTTS = async () => {
    if (ttsPlaying || ttsLoading || !responseLanguage) return;
    setTtsLoading(true);
    try {
      const audioBlob = await textToSpeech(message.text, responseLanguage);
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      setTtsPlaying(true);
      audio.onended = () => {
        setTtsPlaying(false);
        URL.revokeObjectURL(url);
      };
      audio.play();
    } catch (err) {
      console.error('TTS failed:', err);
    } finally {
      setTtsLoading(false);
    }
  };

  // Simple markdown-like rendering
  const renderText = (text: string) => {
    // Split by code blocks
    const parts = text.split(/(```[\s\S]*?```)/g);

    return parts.map((part, index) => {
      if (part.startsWith('```') && part.endsWith('```')) {
        // Code block
        const code = part.slice(3, -3).replace(/^\w+\n/, ''); // Remove language identifier
        return (
          <pre
            key={index}
            className="p-3 rounded-lg my-2 overflow-x-auto text-sm"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <code>{code}</code>
          </pre>
        );
      }

      // Regular text with basic formatting
      return (
        <span key={index}>
          {part.split('\n').map((line, lineIdx) => {
            // Bold text
            let formatted = line.replace(
              /\*\*(.*?)\*\*/g,
              '<strong>$1</strong>'
            );
            // Italic text
            formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');

            return (
              <React.Fragment key={lineIdx}>
                {lineIdx > 0 && <br />}
                <span dangerouslySetInnerHTML={{ __html: formatted }} />
              </React.Fragment>
            );
          })}
        </span>
      );
    });
  };

  return (
    <div
      className={`group flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div
        className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-3 max-w-[85%]`}
      >
        {/* Avatar */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: isUser ? colors.primary : colors.grayScale400,
          }}
        >
          {isUser ? (
            React.createElement(IoPersonCircle as any, { size: 20, color: colors.white })
          ) : (
            React.createElement(IoSparkles as any, { size: 16, color: colors.white })
          )}
        </div>

        {/* Message Content */}
        <div className="flex flex-col">
          {!isUser && message.evaluation_trace && message.evaluation_trace.length > 0 && (
            <EvaluationTrace steps={message.evaluation_trace} colors={colors} />
          )}
          <div
            className={`p-4 rounded-2xl ${isUser ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
            style={{
              backgroundColor: isUser ? colors.primary : colors.inputBackground,
              border: isUser ? 'none' : `1px solid ${colors.borderColor}`,
            }}
          >
            <CText
              type="r14"
              color={isUser ? colors.white : colors.textColor}
              style={{ lineHeight: 1.6 }}
            >
              {renderText(displayText)}
            </CText>
          </div>

          {/* Timestamp + action buttons */}
          <div
            className={`flex items-center gap-2 mt-1 ${isUser ? 'justify-end' : 'justify-start'}`}
          >
            <CText
              type="r12"
              color={colors.grayScale400}
            >
              {formatTime(message.created_at)}
            </CText>

            {/* Edit button for user messages */}
            {isUser && onEdit && (
              <button
                onClick={() => onEdit(message.text)}
                className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10"
                title="Edit message"
              >
                {React.createElement(IoPencil as any, { size: 14, color: colors.grayScale400 })}
              </button>
            )}

            {/* Regenerate button for last assistant message */}
            {!isUser && isLastAssistant && onRegenerate && (
              <button
                onClick={onRegenerate}
                className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10"
                title="Regenerate response"
              >
                {React.createElement(IoRefresh as any, { size: 14, color: colors.grayScale400 })}
              </button>
            )}

            {/* Language toggle — View in English / View in [Language] */}
            {hasTranslation && (
              <button
                onClick={() => setShowEnglish(!showEnglish)}
                className="flex items-center gap-1 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10"
                title={showEnglish ? `View in ${langLabel}` : 'View in English'}
              >
                {React.createElement(IoLanguage as any, { size: 14, color: colors.primary })}
                <span className="text-xs" style={{ color: colors.primary }}>
                  {showEnglish ? langLabel : 'English'}
                </span>
              </button>
            )}

            {/* Speaker button (TTS) for non-English assistant messages */}
            {!isUser && responseLanguage && responseLanguage !== 'en-IN' && (
              <button
                onClick={handleTTS}
                disabled={ttsLoading || ttsPlaying}
                className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10"
                title={ttsPlaying ? 'Playing...' : 'Listen'}
                style={{ opacity: ttsLoading ? 0.5 : undefined }}
              >
                {React.createElement(IoVolumeHigh as any, {
                  size: 14,
                  color: ttsPlaying ? colors.primary : colors.grayScale400,
                })}
              </button>
            )}
          </div>

          {/* Sources (only for assistant messages) */}
          {!isUser && message.sources && message.sources.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setShowSources(!showSources)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <CText type="r12" color={colors.grayScale500}>
                  {message.sources.length} sources
                </CText>
                {showSources ? (
                  React.createElement(IoChevronUp as any, { size: 14, color: colors.grayScale500 })
                ) : (
                  React.createElement(IoChevronDown as any, { size: 14, color: colors.grayScale500 })
                )}
              </button>

              {showSources && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {message.sources.map((source, idx) => (
                    <SourceCard key={idx} source={source} colors={colors} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Star Rating (assistant messages with a DB id) */}
          {!isUser && message.id && onFeedback && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <StarRating
                messageId={message.id}
                existingRating={feedbackRating}
                existingComment={feedbackComment}
                colors={colors}
                onSubmit={onFeedback}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
